import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, inr, fmtDate } from "@/lib/api";
import { PageHeader, Card, Field, Btn, DataTable, FilterBar } from "@/components/shared";
import { Statement } from "@/pages/Reports";
import { KYC_DOCS, KycDocsPanel } from "@/components/KycDocs";

const call = async (fn, msg) => {
  try { const r = await fn(); if (msg) toast.success(msg); return r || true; } catch (e) { toast.error(errMsg(e)); return false; }
};

function Modal({ open, onClose, title, testid, children, wide }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={wide ? "max-w-2xl" : "max-w-md"} data-testid={testid} aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

const Input = ({ label, testid, ...p }) => <Field label={label}><input className="np-input" data-testid={testid} {...p} /></Field>;

const rs = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const range = (l) => (l?.payout ? `${rs(l.payout.min)} – ${rs(l.payout.max)}` : "Role default");

function PayoutLimits({ user }) {
  const [lim, setLim] = useState({ min: "", max: "" });
  const [eff, setEff] = useState(null);
  const [hist, setHist] = useState([]);
  const load = () => {
    api.get(`/admin/limits/user/${user.id}`).then(({ data }) => { setEff(data); setLim({ min: data.effective.payout.min, max: data.effective.payout.max }); });
    api.get(`/admin/limits/user/${user.id}/history`).then(({ data }) => setHist(data));
  };
  useEffect(() => { load(); }, [user.id]); // eslint-disable-line
  const reset = async () => {
    const { payout, ...rest } = eff.override || {};
    if (await call(() => api.put(`/admin/limits/user/${user.id}`, { limits: Object.keys(rest).length ? rest : null }), "Back to default limit")) load();
  };
  const save = async () => { if (await call(() => api.put(`/admin/limits/user/${user.id}/payout`, { min: Number(lim.min), max: Number(lim.max) }), "Move to Bank limit updated")) load(); };
  return (
    <div className="mt-2 border-t border-slate-100 pt-4" data-testid="profile-payout-limits">
      <div className="text-sm font-semibold">Move to Bank Limits <span className="text-xs font-normal text-slate-500">(per transaction)</span></div>
      <div className="mt-1 text-xs text-slate-500" data-testid="profile-payout-limit-current">Current: {eff ? `${rs(eff.effective.payout.min)} – ${rs(eff.effective.payout.max)}` : "…"} {eff && (eff.override?.payout ? <span className="font-semibold text-blue-700">(custom)</span> : "(role default)")}</div>
      <div className="mt-2 flex items-end gap-2">
        <Input label="Minimum ₹" type="number" min={1001} max={100000} value={lim.min} onChange={(e) => setLim({ ...lim, min: e.target.value })} testid="profile-payout-min" />
        <Input label="Maximum ₹" type="number" min={1001} max={100000} value={lim.max} onChange={(e) => setLim({ ...lim, max: e.target.value })} testid="profile-payout-max" />
        <Btn variant="outline" onClick={save} disabled={!lim.min || !lim.max} data-testid="profile-payout-limit-save">Save limit</Btn>
        {eff?.override?.payout && <Btn variant="outline" onClick={reset} data-testid="profile-payout-limit-reset">Reset to default</Btn>}
      </div>
      <div className="mt-1 text-[11px] text-slate-500">Allowed: ₹1,001 – ₹1,00,000. Default: ₹1,001 – ₹50,000.</div>
      <div className="mt-3 max-h-40 overflow-y-auto text-xs" data-testid="profile-limit-history">
        <div className="mb-1 font-semibold text-slate-600">Change history</div>
        {!hist.length && <div className="text-slate-400">No changes yet</div>}
        {hist.map((h) => (
          <div key={h.id} className="border-t border-slate-100 py-1.5" data-testid={`limit-history-${h.id}`}>
            <b>{range(h.old)}</b> → <b>{range(h.new)}</b> · by {h.by_name} ({h.by_login}) · {fmtDate(h.at)}
          </div>
        ))}
      </div>
    </div>
  );
}

export function EditUserDialog({ user, onClose }) {
  const [f, setF] = useState({});
  const [mpin, setMpin] = useState("");
  useEffect(() => { if (user) { setF({ name: user.name, mobile: user.mobile, email: user.email || "", shop_name: user.shop_name || "", city: user.city || "", state: user.state || "" }); setMpin(""); } }, [user]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => { if (await call(() => api.put(`/admin/users/${user.id}`, f), "Member details updated")) onClose(); };
  const changeMpin = async () => { if (await call(() => api.post(`/admin/users/${user.id}/reset-mpin`, { mpin }), "MPIN changed")) setMpin(""); };
  return (
    <Modal open={!!user} onClose={onClose} title={`Edit · ${user?.login_id || ""}`} testid="edit-user-dialog" wide>
      <div className="grid gap-3 sm:grid-cols-2">
        <Input label="Name" value={f.name || ""} onChange={set("name")} testid="edit-user-name" />
        <Input label="Mobile" value={f.mobile || ""} onChange={set("mobile")} maxLength={10} testid="edit-user-mobile" />
        <Input label="Email" value={f.email || ""} onChange={set("email")} testid="edit-user-email" />
        <Input label="Shop / Firm" value={f.shop_name || ""} onChange={set("shop_name")} testid="edit-user-shop" />
        <Input label="City" value={f.city || ""} onChange={set("city")} testid="edit-user-city" />
        <Input label="State" value={f.state || ""} onChange={set("state")} testid="edit-user-state" />
      </div>
      <Btn onClick={save} data-testid="edit-user-save">Save details</Btn>
      <div className="mt-2 flex items-end gap-2 border-t border-slate-100 pt-4">
        <Input label="New MPIN (4 digits)" type="password" inputMode="numeric" maxLength={4} value={mpin} onChange={(e) => setMpin(e.target.value.replace(/\D/g, ""))} testid="edit-user-mpin" />
        <Btn variant="outline" onClick={changeMpin} disabled={mpin.length !== 4} data-testid="edit-user-mpin-save">Change MPIN</Btn>
      </div>
      {user && <PayoutLimits user={user} />}
    </Modal>
  );
}

function UserPicker({ value, onPick }) {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  useEffect(() => { const t = setTimeout(() => api.get("/admin/users-search", { params: { q } }).then(({ data }) => setList(data)), 250); return () => clearTimeout(t); }, [q]);
  return (
    <Card title="Select member">
      <input className="np-input max-w-sm" placeholder="Search login ID, name, mobile or shop" value={q} onChange={(e) => setQ(e.target.value)} data-testid="statement-user-search" />
      <div className="mt-3 flex flex-wrap gap-2">
        {list.map((u) => (
          <button key={u.id} onClick={() => onPick(u)} className={`rounded-md border px-3 py-1.5 text-xs ${value?.id === u.id ? "border-blue-600 bg-blue-50 text-blue-800" : "border-slate-200 bg-white"}`} data-testid={`statement-pick-${u.login_id}`}>
            <b className="font-mono">{u.login_id}</b> · {u.name} · <span className="text-slate-500">{u.role}</span>
          </button>
        ))}
      </div>
    </Card>
  );
}

export function AdminStatement() {
  const [u, setU] = useState(null);
  return (
    <div>
      <UserPicker value={u} onPick={setU} />
      <div className="mt-6">
        {u ? <Statement url={`/admin/users/${u.id}/statement`} title={`Agent Statement · ${u.login_id}`} subtitle={`${u.name} · Main ${inr(u.balance)} · AEPS ${inr(u.aeps_balance)}`} />
          : <div className="text-sm text-slate-500" data-testid="statement-pick-hint">Pick a member above to see their full statement.</div>}
      </div>
    </div>
  );
}

const BLANK_BANK = { bank: "", account_name: "", account_no: "", ifsc: "", branch: "", active: true, visible_to: [], is_credit_bank: false };

export function CompanyBanks() {
  const [rows, setRows] = useState([]);
  const [net, setNet] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/company-banks").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
    Promise.all(["super", "distributor", "sr"].map((r) => api.get("/admin/users", { params: { role: r } }))).then((res) => setNet(res.flatMap((x) => x.data)));
  }, []);
  const name = (id) => net.find((n) => n.id === id)?.login_id || id.slice(0, 6);
  const save = async () => {
    const fn = () => (edit.id ? api.put(`/admin/company-banks/${edit.id}`, edit) : api.post("/admin/company-banks", edit));
    if (await call(fn, "Bank saved")) { setEdit(null); load(); }
  };
  const del = async (b) => { if (window.confirm(`Remove ${b.bank} ${b.account_no}?`) && (await call(() => api.delete(`/admin/company-banks/${b.id}`), "Bank removed"))) load(); };
  const set = (k) => (e) => setEdit({ ...edit, [k]: k === "ifsc" ? e.target.value.toUpperCase() : e.target.value });
  const toggleNet = (id) => setEdit({ ...edit, visible_to: edit.visible_to.includes(id) ? edit.visible_to.filter((x) => x !== id) : [...edit.visible_to, id] });
  return (
    <div>
      <PageHeader title="Company Banks" subtitle="Bank accounts where members deposit money for Fund Requests. Choose who sees each account.">
        <Btn onClick={() => setEdit(BLANK_BANK)} data-testid="company-bank-add"><Plus className="h-4 w-4" />Add bank</Btn>
      </PageHeader>
      <DataTable testid="company-banks-table" rows={rows} columns={[
        { label: "Bank", key: "bank" }, { label: "Account Name", key: "account_name" },
        { label: "Account No.", render: (b) => <span className="font-mono text-xs">{b.account_no}</span> },
        { label: "IFSC", render: (b) => <span className="font-mono text-xs">{b.ifsc}</span> }, { label: "Branch", key: "branch" },
        { label: "Visible to", render: (b) => (b.visible_to?.length ? <span className="text-xs">{b.visible_to.map(name).join(", ")} + their network</span> : <span className="text-xs text-slate-500">Everyone</span>) },
        { label: "Status", render: (b) => <span className={`text-xs font-bold ${b.active ? "text-emerald-700" : "text-slate-400"}`}>{b.active ? "ACTIVE" : "HIDDEN"}{b.is_credit_bank ? " · CREDIT" : ""}</span> },
        { label: "Action", render: (b) => (
          <div className="flex gap-3 text-xs font-semibold">
            <button className="text-blue-700" onClick={() => setEdit({ ...BLANK_BANK, ...b })} data-testid={`company-bank-edit-${b.account_no}`}>Edit</button>
            <button className="text-red-600" onClick={() => del(b)} data-testid={`company-bank-delete-${b.account_no}`}>Remove</button>
          </div>) },
      ]} />
      <Modal open={!!edit} onClose={() => setEdit(null)} title={edit?.id ? "Edit bank" : "Add bank"} testid="company-bank-dialog" wide>
        {edit && (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="Bank name" value={edit.bank} onChange={set("bank")} testid="company-bank-name" />
              <Input label="Account holder name" value={edit.account_name} onChange={set("account_name")} testid="company-bank-holder" />
              <Input label="Account number" value={edit.account_no} onChange={set("account_no")} testid="company-bank-account" />
              <Input label="IFSC" maxLength={11} value={edit.ifsc} onChange={set("ifsc")} testid="company-bank-ifsc" />
              <Input label="Branch" value={edit.branch} onChange={set("branch")} testid="company-bank-branch" />
              <label className="flex items-center gap-2 pt-6 text-sm"><input type="checkbox" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} data-testid="company-bank-active" />Show to members</label>
              <label className="flex items-center gap-2 pt-6 text-sm"><input type="checkbox" checked={!!edit.is_credit_bank} onChange={(e) => setEdit({ ...edit, is_credit_bank: e.target.checked })} data-testid="company-bank-credit" />Credit bank (recoverable credit, due in 12h)</label>
            </div>
            <div>
              <div className="np-label">Visible to (leave empty for everyone)</div>
              <div className="mt-2 flex max-h-40 flex-wrap gap-2 overflow-y-auto">
                {net.map((n) => (
                  <button type="button" key={n.id} onClick={() => toggleNet(n.id)} className={`rounded-md border px-2 py-1 text-xs ${edit.visible_to.includes(n.id) ? "border-blue-600 bg-blue-50 text-blue-800" : "border-slate-200"}`} data-testid={`company-bank-visible-${n.login_id}`}>{n.login_id} · {n.name}</button>
                ))}
              </div>
            </div>
            <Btn onClick={save} data-testid="company-bank-save">Save bank</Btn>
          </>
        )}
      </Modal>
    </div>
  );
}

export function SalesReport() {
  const [rows, setRows] = useState([]);
  const [group, setGroup] = useState("user");
  const [loading, setLoading] = useState(false);
  const load = async (f = {}) => {
    setLoading(true);
    try { const { data } = await api.get("/admin/reports/sales", { params: { ...f, group } }); setRows(data); } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [group]); // eslint-disable-line
  const svc = (r) => Object.entries(r.services).map(([k, v]) => `${k}: ${v.count} / ${inr(v.amount)}`).join(" · ");
  const cols = [
    { label: group === "user" ? "Member" : "Distributor", render: (r) => <span><b className="font-mono text-xs">{r.login_id}</b> {r.name}</span>, csv: (r) => `${r.login_id} ${r.name}` },
    { label: "Role", key: "role" }, { label: "Txns", key: "count" },
    { label: "Amount", key: "amount", render: (r) => <span className="font-mono font-semibold">{inr(r.amount)}</span> },
    { label: "Commission", key: "commission", render: (r) => <span className="font-mono">{inr(r.commission)}</span> },
    { label: "Charges", key: "charge", render: (r) => <span className="font-mono">{inr(r.charge)}</span> },
    { label: "By service", render: (r) => <span className="text-xs text-slate-500">{svc(r)}</span>, csv: svc },
  ];
  return (
    <div>
      <PageHeader title="Sales Report" subtitle="Successful transactions by member or by distributor for any date range.">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
          {[["user", "Member wise"], ["distributor", "Distributor wise"]].map(([v, l]) => (
            <button key={v} onClick={() => setGroup(v)} className={`rounded-md px-3 py-1.5 text-xs font-semibold ${group === v ? "bg-slate-900 text-white" : "text-slate-600"}`} data-testid={`sales-group-${v}`}>{l}</button>
          ))}
        </div>
      </PageHeader>
      <FilterBar onSearch={load} loading={loading} exportRows={rows} exportName="sales-report" exportCols={cols} />
      <DataTable testid="sales-report-table" rows={rows} columns={cols} />
    </div>
  );
}

export function FundSummary() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async (f = {}) => {
    setLoading(true);
    try { const { data } = await api.get("/admin/reports/fund-summary", { params: f }); setD(data); } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const total = (d?.balances || []).reduce((a, b) => a + b.main + b.aeps, 0);
  return (
    <div>
      <PageHeader title="Fund Summary" subtitle="Wallet balances held by members, and money in / out for the selected dates." />
      <FilterBar onSearch={load} loading={loading} />
      {d && (
        <div className="space-y-6">
          <Card title={`Wallet balances · total ${inr(total)}`}>
            <DataTable testid="fund-balances-table" rows={d.balances} columns={[
              { label: "Role", key: "role" }, { label: "Members", key: "users" },
              { label: "Main wallet", render: (r) => <span className="font-mono">{inr(r.main)}</span> },
              { label: "AEPS wallet", render: (r) => <span className="font-mono">{inr(r.aeps)}</span> },
            ]} />
          </Card>
          <Card title="Money in / out (ledger)">
            <DataTable testid="fund-ledger-table" rows={d.ledger} columns={[
              { label: "Service", key: "service" },
              { label: "Type", render: (r) => <span className={`text-xs font-bold ${r.type === "CR" ? "text-emerald-700" : "text-red-600"}`}>{r.type === "CR" ? "CREDIT" : "DEBIT"}</span> },
              { label: "Entries", key: "count" }, { label: "Amount", render: (r) => <span className="font-mono">{inr(r.amount)}</span> },
            ]} />
          </Card>
          <Card title="Fund requests">
            <DataTable testid="fund-requests-summary-table" rows={d.fund_requests} columns={[
              { label: "Status", key: "status" }, { label: "Requests", key: "count" }, { label: "Amount", render: (r) => <span className="font-mono">{inr(r.amount)}</span> },
            ]} />
          </Card>
        </div>
      )}
    </div>
  );
}

export function AdminLogs() {
  const [kind, setKind] = useState("login");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const load = async (f = {}) => {
    setLoading(true);
    try { const { data } = await api.get("/admin/logs", { params: { ...f, kind, q } }); setRows(data); } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [kind]); // eslint-disable-line
  const cols = kind === "login" ? [
    { label: "Time", render: (r) => fmtDate(r.created_at), csv: (r) => r.created_at }, { label: "Login", key: "login" }, { label: "Panel", key: "panel" },
    { label: "Result", render: (r) => <span className={`text-xs font-bold ${r.success ? "text-emerald-700" : "text-red-600"}`}>{r.success ? "SUCCESS" : "FAILED"}</span>, csv: (r) => (r.success ? "SUCCESS" : "FAILED") },
    { label: "IP", key: "ip" }, { label: "Device", render: (r) => <span className="text-xs text-slate-500">{r.agent}</span>, csv: (r) => r.agent },
  ] : [
    { label: "Time", render: (r) => fmtDate(r.created_at), csv: (r) => r.created_at }, { label: "By", key: "login_id" }, { label: "Role", key: "role" },
    { label: "Action", render: (r) => <span className="font-mono text-xs">{r.method} {r.path}</span>, csv: (r) => `${r.method} ${r.path}` }, { label: "IP", key: "ip" },
  ];
  return (
    <div>
      <PageHeader title="Logs" subtitle="Who logged in from where, and every change made from the admin panel.">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
          {[["login", "Login logs"], ["action", "Admin actions"]].map(([v, l]) => (
            <button key={v} onClick={() => setKind(v)} className={`rounded-md px-3 py-1.5 text-xs font-semibold ${kind === v ? "bg-slate-900 text-white" : "text-slate-600"}`} data-testid={`logs-kind-${v}`}>{l}</button>
          ))}
        </div>
      </PageHeader>
      <FilterBar onSearch={load} loading={loading} exportRows={rows} exportName={`${kind}-logs`} exportCols={cols}>
        <Field label="Login / ID"><input className="np-input" value={q} onChange={(e) => setQ(e.target.value)} data-testid="logs-search" /></Field>
      </FilterBar>
      <DataTable testid="logs-table" rows={rows} columns={cols} />
    </div>
  );
}

function PermBoxes({ perms, value, onChange, prefix }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {Object.entries(perms).map(([k, l]) => (
        <label key={k} className="flex items-center gap-2 rounded border border-slate-200 px-3 py-2 text-xs">
          <input type="checkbox" checked={value.includes(k)} onChange={() => toggle(k)} data-testid={`${prefix}-perm-${k}`} />{l}
        </label>
      ))}
    </div>
  );
}

export function SubAdmins() {
  const [d, setD] = useState({ perms: {}, users: [] });
  const [create, setCreate] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/subadmins").then(({ data }) => setD(data));
  useEffect(() => { load(); }, []);
  const add = async () => { if (await call(() => api.post("/admin/subadmins", create), "Sub-admin created")) { setCreate(null); load(); } };
  const save = async () => { if (await call(() => api.patch(`/admin/subadmins/${edit.id}`, { permissions: edit.permissions, status: edit.status }), "Sub-admin updated")) { setEdit(null); load(); } };
  const pwd = async (u) => { const p = window.prompt(`New password for ${u.login_id} (min 6 chars)`); if (p) await call(() => api.post(`/admin/subadmins/${u.id}/password`, { password: p }), "Password changed"); };
  return (
    <div>
      <PageHeader title="Sub-Admins" subtitle="Staff logins with only the pages you allow. They sign in from your secret admin link.">
        <Btn onClick={() => setCreate({ name: "", mobile: "", email: "", password: "", permissions: [] })} data-testid="subadmin-add"><Plus className="h-4 w-4" />New sub-admin</Btn>
      </PageHeader>
      <DataTable testid="subadmins-table" rows={d.users} columns={[
        { label: "Login ID", render: (u) => <span className="font-mono text-xs">{u.login_id}</span> }, { label: "Name", key: "name" }, { label: "Mobile", key: "mobile" },
        { label: "Pages allowed", render: (u) => <span className="text-xs">{(u.permissions || []).map((p) => d.perms[p] || p).join(", ") || "None"}</span> },
        { label: "Status", render: (u) => <span className={`text-xs font-bold ${u.status === "blocked" ? "text-red-600" : "text-emerald-700"}`}>{(u.status || "active").toUpperCase()}</span> },
        { label: "Action", render: (u) => (
          <div className="flex gap-3 text-xs font-semibold">
            <button className="text-blue-700" onClick={() => setEdit({ ...u, permissions: u.permissions || [], status: u.status || "active" })} data-testid={`subadmin-edit-${u.login_id}`}>Edit</button>
            <button className="text-slate-700" onClick={() => pwd(u)} data-testid={`subadmin-password-${u.login_id}`}>Password</button>
            {u.mfa_enabled && <button className="text-red-700" onClick={() => window.confirm(`Reset 2FA for ${u.login_id}? They must scan a new QR on next login.`) && call(() => api.post(`/auth/mfa/reset/${u.id}`), "2FA reset").then(load)} data-testid={`subadmin-mfa-reset-${u.login_id}`}>Reset 2FA</button>}
          </div>) },
        { label: "2FA", render: (u) => <span className={`text-xs font-bold ${u.mfa_enabled ? "text-emerald-700" : "text-amber-600"}`} data-testid={`subadmin-mfa-${u.login_id}`}>{u.mfa_enabled ? "ON" : "Setup pending"}</span> },
      ]} />
      <Modal open={!!create} onClose={() => setCreate(null)} title="New sub-admin" testid="subadmin-create-dialog" wide>
        {create && (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="Name" value={create.name} onChange={(e) => setCreate({ ...create, name: e.target.value })} testid="subadmin-name" />
              <Input label="Mobile (login)" maxLength={10} value={create.mobile} onChange={(e) => setCreate({ ...create, mobile: e.target.value })} testid="subadmin-mobile" />
              <Input label="Email" value={create.email} onChange={(e) => setCreate({ ...create, email: e.target.value })} testid="subadmin-email" />
              <Input label="Password" type="password" value={create.password} onChange={(e) => setCreate({ ...create, password: e.target.value })} testid="subadmin-password" />
            </div>
            <PermBoxes perms={d.perms} value={create.permissions} onChange={(v) => setCreate({ ...create, permissions: v })} prefix="subadmin-create" />
            <Btn onClick={add} data-testid="subadmin-create-save">Create</Btn>
          </>
        )}
      </Modal>
      <Modal open={!!edit} onClose={() => setEdit(null)} title={`Edit · ${edit?.login_id || ""}`} testid="subadmin-edit-dialog" wide>
        {edit && (
          <>
            <PermBoxes perms={d.perms} value={edit.permissions} onChange={(v) => setEdit({ ...edit, permissions: v })} prefix="subadmin-edit" />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={edit.status === "blocked"} onChange={(e) => setEdit({ ...edit, status: e.target.checked ? "blocked" : "active" })} data-testid="subadmin-edit-blocked" />Block this login</label>
            <Btn onClick={save} data-testid="subadmin-edit-save">Save</Btn>
          </>
        )}
      </Modal>
    </div>
  );
}

export function AdminApprovals() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [view, setView] = useState(null);
  const load = () => api.get("/admin/approvals", { params: { status } }).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line
  const decide = async (u, action) => {
    const remark = action === "reject" ? window.prompt(`Reason for rejecting ${u.login_id}?`) : "";
    if (remark === null) return;
    if (await call(() => api.post(`/admin/approvals/${u.id}`, { action, remark }), action === "approve" ? `${u.login_id} approved` : `${u.login_id} rejected`)) { setView(null); load(); }
  };
  return (
    <div>
      <PageHeader title="Member Approvals" subtitle="Members created by Distributors / Super Distributors can log in only after you approve them.">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
          {[["pending", "Pending"], ["rejected", "Rejected"]].map(([v, l]) => (
            <button key={v} onClick={() => setStatus(v)} className={`rounded-md px-3 py-1.5 text-xs font-semibold ${status === v ? "bg-slate-900 text-white" : "text-slate-600"}`} data-testid={`approvals-tab-${v}`}>{l}</button>
          ))}
        </div>
      </PageHeader>
      <DataTable testid="approvals-table" rows={rows} columns={[
        { label: "Date", render: (u) => <span className="text-slate-500">{fmtDate(u.created_at)}</span> },
        { label: "Login ID", render: (u) => <span className="font-mono text-xs">{u.login_id}</span> },
        { label: "Name", key: "name" }, { label: "Role", key: "role" }, { label: "Mobile", key: "mobile" },
        { label: "Shop / City", render: (u) => `${u.shop_name || "-"} · ${u.city || "-"}` },
        { label: "Created by", render: (u) => (u.parent ? `${u.parent.login_id} ${u.parent.name}` : "-") },
        { label: "KYC docs", render: (u) => { const n = KYC_DOCS.filter(([k]) => u.kyc_docs?.[k]).length; return <span className={`text-xs font-bold ${n === KYC_DOCS.length ? "text-emerald-700" : "text-red-600"}`} data-testid={`kyc-count-${u.login_id}`}>{n}/{KYC_DOCS.length}</span>; } },
        ...(status === "rejected" ? [{ label: "Reason", key: "approval_remark" }] : []),
        { label: "Action", render: (u) => (
          <div className="flex gap-3 text-xs font-semibold">
            <button className="text-blue-700" onClick={() => setView(u)} data-testid={`view-kyc-${u.login_id}`}>View KYC</button>
            <button className="text-emerald-700" onClick={() => decide(u, "approve")} data-testid={`approve-${u.login_id}`}>Approve</button>
            {status === "pending" && <button className="text-red-600" onClick={() => decide(u, "reject")} data-testid={`reject-${u.login_id}`}>Reject</button>}
          </div>) },
      ]} />
      <Modal open={!!view} onClose={() => setView(null)} title={`KYC · ${view?.login_id || ""} ${view?.name || ""}`} testid="approval-kyc-dialog" wide>
        {view && (
          <>
            <KycDocsPanel user={view} uploadUrl={`/admin/users/${view.id}/kyc-docs`} onChange={load} />
            <div className="flex justify-end gap-2">
              {status === "pending" && <Btn variant="outline" onClick={() => decide(view, "reject")} data-testid="approval-dialog-reject">Reject</Btn>}
              <Btn onClick={() => decide(view, "approve")} data-testid="approval-dialog-approve">Approve</Btn>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
